import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  UsersIcon,
  CheckIcon,
  BanknotesIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import EnhancedLeadAssignmentModal from './EnhancedLeadAssignmentModal';
import StatusChangeModal from './StatusChangeModal';
import { useFormFields } from '../hooks/useFormFields';
import { leadsApi } from '../config/api';

const LeadList = ({ leads, onEdit, onDelete, onStatusChange, onScheduleFollowUp, onAssignmentComplete, onAddFollowUp, onViewFollowUpHistory, onBulkAssignment, bulkSelectMode: externalBulkSelectMode, setBulkSelectMode: setExternalBulkSelectMode }) => {
  const navigate = useNavigate();
  
  // Form fields hook
  const { fields: formFields, loading: fieldsLoading } = useFormFields();
  
  // Assignment states
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  // Use external bulkSelectMode if provided, otherwise use internal state
  const bulkSelectMode = externalBulkSelectMode !== undefined ? externalBulkSelectMode : false;
  const setBulkSelectMode = setExternalBulkSelectMode || (() => {});
  
  // Lead status states
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  
  // Status change modal states
  const [statusChangeModal, setStatusChangeModal] = useState({
    isOpen: false,
    lead: null,
    newStatus: '',
    viewHistoryOnly: false
  });

  // Fetch lead statuses on component mount
  useEffect(() => {
    fetchLeadStatuses();
  }, []);

  const fetchLeadStatuses = async () => {
    try {
      setStatusLoading(true);
      console.log('🔍 Fetching lead statuses from:', leadsApi.defaults.baseURL + '/lead-statuses');
      const response = await leadsApi.get('/lead-statuses');
      console.log('📊 Lead statuses response:', response.data);
      if (response.data.success) {
        setLeadStatuses(response.data.statuses);
        console.log('✅ Lead statuses loaded:', response.data.statuses.length);
        console.log('📋 Status details:', response.data.statuses.map(s => ({
          name: s.name,
          label: s.label,
          color: s.color,
          canTransitionTo: s.canTransitionTo
        })));
      } else {
        console.error('❌ API returned success: false');
      }
    } catch (error) {
      console.error('❌ Error fetching lead statuses:', error);
      console.error('❌ Error details:', error.response?.data);
    } finally {
      setStatusLoading(false);
    }
  };

  const getStatusColor = (status) => {
    // Find the status in our dynamic statuses
    const statusObj = leadStatuses.find(s => s.name === status);
    if (statusObj) {
      return `bg-${statusObj.color}-100 text-${statusObj.color}-800`;
    }
    
    // Fallback to hardcoded colors for backward compatibility
    switch (status) {
      case 'new': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-purple-100 text-purple-800';
      case 'qualified': return 'bg-indigo-100 text-indigo-800';
      case 'proposal': return 'bg-blue-100 text-blue-800';
      case 'negotiation': return 'bg-orange-100 text-orange-800';
      case 'closed_won': return 'bg-green-100 text-green-800';
      case 'closed_lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOutcomeIcon = (status) => {
    const statusObj = leadStatuses.find(s => s.name === status);
    if (statusObj && statusObj.isFinal) {
      if (statusObj.outcome === 'converted') {
        return { icon: '', text: 'Converted', color: 'text-green-600' };
      } else if (statusObj.outcome === 'rejected') {
        return { icon: '', text: 'Rejected', color: 'text-red-600' };
      } else if (statusObj.outcome === 'on_hold') {
        return { icon: '', text: 'On Hold', color: 'text-yellow-600' };
      }
    }
    return null;
  };

  const getColorStyles = (color) => {
    const colorMap = {
      'blue': { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' },
      'yellow': { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
      'green': { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' },
      'emerald': { bg: '#D1FAE5', text: '#047857', border: '#6EE7B7' },
      'purple': { bg: '#E9D5FF', text: '#6B21A8', border: '#C084FC' },
      'orange': { bg: '#FED7AA', text: '#9A3412', border: '#FDBA74' },
      'red': { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' },
      'amber': { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
      'indigo': { bg: '#E0E7FF', text: '#3730A3', border: '#A5B4FC' },
      'gray': { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' },
      'slate': { bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' },
    };
    return colorMap[color] || colorMap['gray'];
  };

  const getAvailableStatusTransitions = (currentStatus) => {
    const currentStatusObj = leadStatuses.find(s => s.name === currentStatus);
    
    if (currentStatusObj && currentStatusObj.canTransitionTo && currentStatusObj.canTransitionTo.length > 0) {
      // Return statuses that current status can transition to, plus current status
      // canTransitionTo contains status names, not ObjectIds
      const availableStatuses = leadStatuses.filter(status => 
        currentStatusObj.canTransitionTo.includes(status.name) || status.name === currentStatus
      );
      return availableStatuses;
    }
    // If no transitions defined, allow all statuses
    return leadStatuses;
  };

  const getSourceLabel = (source) => {
    const sourceMap = {
      'website': 'Website',
      'instagram': 'Instagram',
      'facebook': 'Facebook',
      'whatsapp': 'WhatsApp',
      'linkedin': 'LinkedIn',
      'twitter': 'Twitter',
      'youtube': 'YouTube',
      'referral': 'Referral',
      'email_campaign': 'Email Campaign',
      'phone_call': 'Phone Call',
      'trade_show': 'Trade Show',
      'advertisement': 'Advertisement',
      'other': 'Other'
    };
    return sourceMap[source] || source;
  };

  const getSourceColor = (source) => {
    const colorMap = {
      'website': 'bg-green-100 text-green-800',
      'instagram': 'bg-pink-100 text-pink-800',
      'facebook': 'bg-blue-100 text-blue-800',
      'whatsapp': 'bg-green-100 text-green-800',
      'linkedin': 'bg-indigo-100 text-indigo-800',
      'twitter': 'bg-sky-100 text-sky-800',
      'youtube': 'bg-red-100 text-red-800',
      'referral': 'bg-purple-100 text-purple-800',
      'email_campaign': 'bg-orange-100 text-orange-800',
      'phone_call': 'bg-teal-100 text-teal-800',
      'trade_show': 'bg-gray-100 text-gray-800',
      'advertisement': 'bg-yellow-100 text-yellow-800',
      'other': 'bg-gray-100 text-gray-800'
    };
    return colorMap[source] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Assignment functions
  const toggleLeadSelection = (lead) => {
    setSelectedLeads(prev => {
      const isSelected = prev.some(l => l._id === lead._id);
      if (isSelected) {
        return prev.filter(l => l._id !== lead._id);
      } else {
        return [...prev, lead];
      }
    });
  };

  const selectAllLeads = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads([...leads]);
    }
  };

  const handleSingleAssign = (lead) => {
    setSelectedLeads([lead]);
    setShowAssignmentModal(true);
  };

  const handleBulkAssign = () => {
    if (selectedLeads.length === 0) {
      alert('Please select leads to assign');
      return;
    }
    // Use the new bulk assignment modal if available, otherwise fallback to existing modal
    if (onBulkAssignment) {
      onBulkAssignment(selectedLeads);
      setSelectedLeads([]);
      setBulkSelectMode(false);
    } else {
      setShowAssignmentModal(true);
    }
  };

  const handleAssignmentComplete = (result) => {
    setSelectedLeads([]);
    setBulkSelectMode(false);
    if (onAssignmentComplete) {
      onAssignmentComplete(result);
    }
  };

  const isLeadSelected = (lead) => {
    return selectedLeads.some(l => l._id === lead._id);
  };

  if (leads.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
        <div className="text-gray-400 mb-4">
          <BuildingOfficeIcon className="h-16 w-16 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
        <p className="text-gray-500">Get started by adding your first lead.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden relative">
      {/* Floating Bulk Assign Button - Shows when leads are selected */}
      {bulkSelectMode && selectedLeads.length > 0 && (
        <div className="sticky top-0 z-50 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <UsersIcon className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-sm">{selectedLeads.length} Lead{selectedLeads.length > 1 ? 's' : ''} Selected</p>
              <p className="text-xs text-blue-100">Ready for bulk assignment</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkAssign}
              className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-blue-50 transition-colors flex items-center gap-2 shadow-md"
            >
              <UsersIcon className="h-4 w-4" />
              Bulk Assign to Employees
            </button>
            <button
              onClick={() => {
                setSelectedLeads([]);
                setBulkSelectMode(false);
              }}
              className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Horizontal Scroll Container with Sticky Scrollbar */}
      <style>{`
        .lead-table-wrapper {
          overflow-x: scroll !important;
          overflow-y: visible;
          -webkit-overflow-scrolling: touch;
          max-height: 600px;
          position: relative;
        }
        /* Always show scrollbar - make it prominent */
        .lead-table-wrapper::-webkit-scrollbar {
          height: 18px;
          background: #F9FAFB;
        }
        .lead-table-wrapper::-webkit-scrollbar-track {
          background: #E5E7EB;
          border: 2px solid #D1D5DB;
          box-shadow: inset 0 0 5px rgba(0,0,0,0.1);
        }
        .lead-table-wrapper::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #6B7280 0%, #4B5563 100%);
          border: 3px solid #E5E7EB;
          border-radius: 10px;
          box-shadow: 0 2px 4₹px rgba(0,0,0,0.2);
        }
        .lead-table-wrapper::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #374151 0%, #1F2937 100%);
        }
        .lead-table-wrapper::-webkit-scrollbar-thumb:active {
          background: #111827;
        }
        /* Firefox scrollbar */
        .lead-table-wrapper {
          scrollbar-width: auto;
          scrollbar-color: #6B7280 #E5E7EB;
        }
        
        /* Sticky scrollbar effect */
        .lead-table-wrapper {
          position: relative;
        }
      `}</style>
      
      <div className="lead-table-wrapper">
        <table className="w-full divide-y divide-gray-200 border-collapse table-fixed" style={{ width: '1830px' }}>
          <thead className="bg-gray-50 sticky top-0 z-20">
            <tr className="divide-x divide-gray-200">
              {bulkSelectMode && (
                <th className="py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12 sticky left-0 bg-gray-50 z-30 shadow-sm border-r border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onChange={selectAllLeads}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
              )}
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-24">
                Date
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-36">
                Title / Purpose
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-28">
                Lead Name
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-44">
                Contact
              </th>
              
              {/* Dynamic Custom Form Fields Headers */}
              {formFields && formFields.filter(field => field.name !== 'title').map((field) => (
                <th key={field._id} className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-32">
                  {field.label}
                </th>
              ))}
              
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-36">
                Status
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-28">
                Budget
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-24">
                Source
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-32">
                Assigned
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-20">
                Added By
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight w-80">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {leads.map((lead) => (
              <tr 
                key={lead._id || lead.id} 
                onClick={(e) => {
                  // Don't navigate if clicking on interactive elements
                  const target = e.target;
                  const isInteractive = target.closest('button') || 
                                       target.closest('a') || 
                                       target.closest('input') || 
                                       target.closest('select');
                  
                  if (!isInteractive) {
                    navigate(`/lead/${lead._id || lead.id}`);
                  }
                }}
                className={`hover:bg-blue-50 cursor-pointer transition-colors divide-x divide-gray-200 ${
                  isLeadSelected(lead) ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
              >
                {bulkSelectMode && (
                  <td className="py-1 whitespace-nowrap border-r border-gray-200">
                    <input
                      type="checkbox"
                      checked={isLeadSelected(lead)}
                      onChange={() => toggleLeadSelection(lead)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                )}
                
                {/* Created Date Column - FIRST */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="text-sm text-gray-900">
                    {lead.createdAt ? formatDate(lead.createdAt) : '-'}
                  </div>
                </td>
                
                {/* Title/Purpose Column */}
                <td 
                  className="py-1 text-center border-r border-gray-200 cursor-pointer" 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/lead/${lead._id || lead.id}`);
                  }}
                >
                  <div className="flex justify-center hover:opacity-80 transition-opacity">
                    {lead.customFields?.title || lead.title ? (
                      <div className="text-sm font-semibold text-blue-800 bg-blue-50 px-2 py-0.5 rounded inline-block border border-blue-200 hover:bg-blue-100 hover:underline">
                        {(lead.customFields?.title || lead.title || '').replace(/\s*-\s*[a-f0-9]{24}$/i, '')}
                      </div>
                    ) : (
                      <div className="text-sm text-red-500 bg-red-50 px-2 py-0.5 rounded inline-block border border-red-200">
                        No purpose
                      </div>
                    )}
                  </div>
                </td>
                
                {/* Lead Name Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="text-sm font-medium text-gray-900">
                    {lead.name}
                  </div>
                </td>
                
                {/* Contact Details Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-900 flex items-center">
                      <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                      <a href={`mailto:${lead.email}`} className="hover:text-blue-600 truncate max-w-[150px]">
                        {lead.email}
                      </a>
                    </div>
                    {lead.phone && (
                      <div className="text-sm text-gray-500 flex items-center">
                        <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <a href={`tel:${lead.phone}`} className="hover:text-blue-600">
                          {lead.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </td>
                
                {/* Dynamic Custom Form Fields Data */}
                {formFields && formFields.filter(field => field.name !== 'title').map((field) => {
                  // Debug log for first lead only
                  if (lead === leads[0]) {
                    console.log(`🔍 Field "${field.name}" (${field.label}):`, lead.customFields?.[field.name]);
                    console.log(`🔍 Full customFields for first lead:`, lead.customFields);
                  }
                  
                  return (
                  <td key={field._id} className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {lead.customFields && lead.customFields[field.name] ? (
                        field.type === 'select' ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {lead.customFields[field.name]}
                          </span>
                        ) : field.type === 'email' ? (
                          <a href={`mailto:${lead.customFields[field.name]}`} className="text-blue-600 hover:text-blue-800">
                            {lead.customFields[field.name]}
                          </a>
                        ) : field.type === 'phone' ? (
                          <a href={`tel:${lead.customFields[field.name]}`} className="text-blue-600 hover:text-blue-800">
                            {lead.customFields[field.name]}
                          </a>
                        ) : field.type === 'textarea' ? (
                          <div className="max-w-32 truncate" title={lead.customFields[field.name]}>
                            {lead.customFields[field.name]}
                          </div>
                        ) : field.type === 'date' ? (
                          <span className="text-gray-900">
                            {new Date(lead.customFields[field.name]).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: '2-digit', 
                              year: 'numeric'
                            })}
                          </span>
                        ) : (
                          <span className="text-gray-900">
                            {lead.customFields[field.name]}
                          </span>
                        )
                      ) : (
                        <span className="text-gray-400 italic text-xs">
                          No {field.label.toLowerCase().replace(/\s+/g, ' ')}
                        </span>
                      )}
                    </div>
                  </td>
                  );
                })}
                
                {/* Status Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="space-y-2 px-2">
                    <div className="flex items-center justify-center gap-2">
                      <select
                        value={lead.status}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          if (newStatus !== lead.status) {
                            setStatusChangeModal({
                              isOpen: true,
                              lead: lead,
                              newStatus: newStatus,
                              viewHistoryOnly: false
                            });
                          }
                        }}
                        className={`inline-flex px-3 py-1.5 text-sm font-bold rounded-full border-2 ${getStatusColor(lead.status)} cursor-pointer`}
                        disabled={statusLoading}
                        title={`Available transitions from ${lead.status}`}
                      >
                      {statusLoading ? (
                        <option>Loading...</option>
                      ) : (
                        getAvailableStatusTransitions(lead.status).map((status) => (
                          <option key={status._id} value={status.name}>
                            {status.label}
                          </option>
                        ))
                      )}
                      {leadStatuses.length === 0 && !statusLoading && (
                        <option disabled>No statuses available</option>
                      )}
                    </select>
                      
                      {/* Outcome Indicator */}
                      {getOutcomeIcon(lead.status) && (
                        <span 
                          className={`inline-flex items-center gap-1 text-xs font-medium ${getOutcomeIcon(lead.status).color}`}
                          title={getOutcomeIcon(lead.status).text}
                        >
                          {getOutcomeIcon(lead.status).icon}
                        </span>
                      )}
                      
                      {/* Status History Button */}
                      {lead.statusHistory && lead.statusHistory.length > 0 && (
                        <button
                          onClick={() => setStatusChangeModal({
                            isOpen: true,
                            lead: lead,
                            newStatus: lead.status,
                            viewHistoryOnly: true
                          })}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                          title={`View status history (${lead.statusHistory.length} changes)`}
                        >
                          <ClockIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Status Flow Indicator */}
                    {!statusLoading && leadStatuses.length > 0 && (() => {
                      const currentStatusObj = leadStatuses.find(s => s.name === lead.status);
                      const transitions = currentStatusObj?.canTransitionTo || [];
                      
                      if (transitions.length > 0) {
                        return (
                          <div className="flex items-center space-x-1 mt-1">
                            <span className="text-xs text-gray-500 font-medium">Next:</span>
                            <div className="flex flex-wrap gap-1">
                              {transitions.slice(0, 3).map((nextStatusName) => {
                                const nextStatus = leadStatuses.find(s => s.name === nextStatusName);
                                if (!nextStatus) {
                                  return null;
                                }
                                const colors = getColorStyles(nextStatus.color);
                                return (
                                  <span 
                                    key={nextStatus._id}
                                    className="inline-flex px-2 py-0.5 text-xs rounded-md font-medium shadow-sm"
                                    style={{
                                      backgroundColor: colors.bg,
                                      color: colors.text,
                                      border: `1px solid ${colors.border}`
                                    }}
                                    title={`Can transition to: ${nextStatus.label}`}
                                  >
                                    {nextStatus.label}
                                  </span>
                                );
                              })}
                              {transitions.length > 3 && (
                                <span className="text-xs text-gray-500 font-medium" title={`${transitions.length - 3} more transitions available`}>
                                  +{transitions.length - 3}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div className="text-xs text-gray-400 italic mt-1">
                           
                          </div>
                        );
                      }
                    })()}
                  </div>
                </td>
                
                {/* Value & Budget Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="space-y-1">
                    {lead.budget && (
                      <div className="text-sm text-gray-900 flex items-center justify-center">
                        <BanknotesIcon className="h-4 w-4 mr-1 text-gray-400" />
                        <span className="font-medium">{lead.budget}</span>
                      </div>
                    )}
                    {!lead.budget && (
                      <div className="text-sm text-gray-500 flex items-center justify-center">
                        <BanknotesIcon className="h-4 w-4 mr-1 text-gray-400" />
                        <span>-</span>
                      </div>
                    )}
                  </div>
                </td>
                
                {/* Source Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceColor(lead.source)}`}>
                    {getSourceLabel(lead.source)}
                  </span>
                  {lead.sourceDetails && (
                    <div className="text-xs text-gray-400 mt-1 truncate max-w-32" title={lead.sourceDetails}>
                      {lead.sourceDetails}
                    </div>
                  )}
                </td>
                
                {/* Assigned To Column - Multiple Employees Support with Dropdown */}
                <td className="py-1 text-center border-r border-gray-200">
                  {(lead.assignedEmployees && lead.assignedEmployees.length > 0) ? (
                    <div className="text-sm relative group">
                      <div className="flex flex-wrap gap-1 cursor-pointer" title={lead.assignedByName ? `Assigned by: ${lead.assignedByName}` : ''}>
                        {lead.assignedEmployees.slice(0, 2).map((emp, idx) => (
                          <div key={idx} className="flex items-center bg-blue-50 rounded-full px-2 py-1" title={emp.email || ''}>
                            <div className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-1">
                              {(emp.name || 'E').charAt(0).toUpperCase()}
                            </div>
                            <span className="text-xs text-gray-700 truncate max-w-[60px]">{emp.name}</span>
                          </div>
                        ))}
                        {lead.assignedEmployees.length > 2 && (
                          <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-1 cursor-pointer hover:bg-gray-200">
                            +{lead.assignedEmployees.length - 2} more
                          </span>
                        )}
                      </div>
                      
                      {/* Dropdown on hover - shows all employees */}
                      {lead.assignedEmployees.length > 0 && (
                        <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block">
                          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[220px]">
                            <div className="text-xs font-semibold text-gray-500 mb-2 pb-1 border-b">
                              Assigned Employees ({lead.assignedEmployees.length})
                            </div>
                            {lead.assignedByName && (
                              <div className="text-xs text-gray-600 mb-2 pb-2 border-b bg-purple-50 p-2 rounded">
                                <span className="font-medium">Assigned by:</span> {lead.assignedByName}
                              </div>
                            )}
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {lead.assignedEmployees.map((emp, idx) => (
                                <div key={idx} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg">
                                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                                    {(emp.name || 'E').charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 text-sm truncate">{emp.name}</div>
                                    {emp.email && (
                                      <div className="text-xs text-gray-500 truncate">{emp.email}</div>
                                    )}
                                    {emp.phone && (
                                      <a href={`tel:${emp.phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                        <PhoneIcon className="h-3 w-3" />
                                        {emp.phone}
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : lead.assignedEmployeeName ? (
                    <div className="text-sm" title={lead.assignedByName ? `Assigned by: ${lead.assignedByName}` : ''}>
                      <div className="flex items-center bg-green-50 rounded-full px-2 py-1">
                        <div className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-1">
                          {(lead.assignedEmployeeName || 'E').charAt(0).toUpperCase()}
                        </div>
                        <span className="text-xs text-gray-700 truncate max-w-[80px]">{lead.assignedEmployeeName}</span>
                      </div>
                      {lead.assignedEmployeePhone && (
                        <div className="text-gray-500 text-xs flex items-center mt-1">
                          <PhoneIcon className="h-3 w-3 mr-1 text-gray-400" />
                          <a href={`tel:${lead.assignedEmployeePhone}`} className="hover:text-blue-600">
                            {lead.assignedEmployeePhone}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">Unassigned</span>
                  )}
                </td>
                
                {/* Created By Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  {lead.createdByName ? (
                    <div className="text-xs" title={`Added by: ${lead.createdByName}${lead.createdByEmail ? ` (${lead.createdByEmail})` : ''}${lead.createdByRole ? `\nRole: ${lead.createdByRole}` : ''}`}>
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                          {(lead.createdByName || 'U').charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="text-gray-700 text-xs mt-0.5 truncate max-w-[70px] mx-auto font-medium">
                        {lead.createdByName}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400" title="Creator information not available">-</span>
                  )}
                </td>
                
                {/* Actions Column */}
                <td className="py-1 text-center">
                  <div className="flex justify-center space-x-2">
                    {/* Assignment Button */}
                    <button
                      onClick={() => handleSingleAssign(lead)}
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-bold rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                      title="Assign to sales person"
                    >
                      <UserPlusIcon className="h-4 w-4 mr-2" />
                      <span>Assign</span>
                    </button>
                    
                    {onScheduleFollowUp && (
                        <button
                          onClick={() => onScheduleFollowUp(lead)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border-2 border-purple-100 shadow-sm"
                          title="Schedule follow-up"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          <span className="text-xs font-bold">Schedule</span>
                        </button>
                    )}
                    {onAddFollowUp && (
                        <button
                          onClick={() => onAddFollowUp(lead)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors border-2 border-green-100 shadow-sm"
                          title="Add follow-up"
                        >
                          <PlusIcon className="h-4 w-4" />
                          <span className="text-xs font-bold">Add</span>
                        </button>
                    )}
                    {onViewFollowUpHistory && (
                      <button
                        onClick={() => onViewFollowUpHistory(lead)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border border-purple-100 shadow-sm"
                        title="View follow-up history"
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        <span className="text-xs font-bold">History</span>
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(lead)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-100 shadow-sm"
                      title="Edit lead"
                    >
                      <PencilIcon className="h-4 w-4" />
                      <span className="text-xs font-bold">Edit</span>
                    </button>
                    <button
                      onClick={() => onDelete(lead._id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-100 shadow-sm"
                      title="Delete lead"
                    >
                      <TrashIcon className="h-4 w-4" />
                      <span className="text-xs font-bold">Delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Notes Section - Expandable */}
      <div className="border-t bg-gray-50">
        <div className="px-6 py-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Notes</h4>
          <div className="space-y-2">
            {leads.slice(0, 3).map((lead) => (
              lead.notes && (
                <div key={lead._id} className="text-sm">
                  <span className="font-medium text-gray-700">{lead.name}:</span>
                  <span className="text-gray-600 ml-2">{lead.notes}</span>
                </div>
              )
            ))}
          </div>
        </div>
      </div>
      
      {/* Lead Assignment Modal */}
      <EnhancedLeadAssignmentModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        selectedLeads={selectedLeads}
        onAssignmentComplete={handleAssignmentComplete}
      />
      
      {/* Status Change Modal with Remark */}
      <StatusChangeModal
        isOpen={statusChangeModal.isOpen}
        onClose={() => setStatusChangeModal({ isOpen: false, lead: null, newStatus: '', viewHistoryOnly: false })}
        lead={statusChangeModal.lead}
        newStatus={statusChangeModal.newStatus}
        statusHistory={statusChangeModal.lead?.statusHistory || []}
        viewHistoryOnly={statusChangeModal.viewHistoryOnly || false}
        onConfirm={(remark) => {
          if (statusChangeModal.lead && statusChangeModal.newStatus) {
            onStatusChange(statusChangeModal.lead._id, statusChangeModal.newStatus, remark);
          }
          setStatusChangeModal({ isOpen: false, lead: null, newStatus: '', viewHistoryOnly: false });
        }}
      />
    </div>
  );
};

export default LeadList;
